/**
 * @fileoverview Карточки решений, медрейс, временное закрытие полос.
 */

import { Snd } from "../audio/sound";
import { canLand, cmdLand } from "../commands/ops";
import { FLIGHT_KINDS } from "../constants/aircraft";
import { frontStage } from "../state/createGame";
import type { GameHooks, GameState, Plane } from "../state/types";
import { pick, rnd, rndi } from "../util/math";
import { killHazardsFor } from "./hazards";
import { spawnPlane } from "../plane/spawn";
import { addScore } from "./scoring";

/** Контекст выполнения опции карточки. */
export interface EventCtx {
  g: GameState;
  hooks: GameHooks;
}

/** Определение события из колоды (до привязки к DOM). */
export interface DecisionDef {
  id: string;
  title: string;
  text: string;
  opts: Array<{
    label: string;
    tip: string;
    run: (ctx: EventCtx) => void;
  }>;
}

/** Временно закрыть полосу (сбой огней). */
export function scheduleRwyTempClose(
  g: GameState,
  hooks: GameHooks,
  ri: number,
  dur: number,
): void {
  const r = g.runways[ri];
  if (!r) return;
  r.closed = true;
  r.tempCloseT = Math.max(r.tempCloseT || 0, dur);
  hooks.toast(`${r.name} временно закрыта из‑за огней`);
}

/** Мягкий отвод борта под медрейс. */
export function divertForMed(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  why: string,
): void {
  if (!p || p.dead) return;
  if (p.status !== "inbound" && p.status !== "approach") return;
  if (p.rwy !== null && g.runways[p.rwy]?.plane === p) {
    g.runways[p.rwy]!.plane = null;
  }
  killHazardsFor(g, p);
  p.rwy = null;
  p.status = "goaround";
  p.gaT = 0;
  p.path = [{ x: p.x + (p.x < 800 ? -1 : 1) * 480, y: -120 }];
  p.pat = Math.max(0.35, p.pat - 0.12);
  g.goarounds++;
  addScore(g, hooks, -25, p, "ОТВОД ПОД MED");
  hooks.toast(`${p.cs} отведен: ${why}`, "wx");
}

/** Спавн медрейса с позывным MED. */
export function spawnMedevac(
  g: GameState,
  hooks: GameHooks,
  label?: string,
): Plane {
  spawnPlane(g, hooks, "jet", false, false);
  const p = g.planes[g.planes.length - 1]!;
  p.kind = "medevac";
  p.FK = FLIGHT_KINDS.medevac;
  p.priority = true;
  p.cs = `MED ${rndi(10, 99)}`;
  p.patRate = 1 / (p.T.pat * 0.42);
  p.medevac = true;
  hooks.selectPlane?.(p);
  hooks.invalidateCard?.();
  hooks.toast(
    `${label || "Медрейс"} ${p.cs} в зоне — полоса с меткой MED`,
    "wx",
  );
  return p;
}

/** Сразу дать заход на зарезервированную полосу, если она свободна. */
function tryAutoLandMed(
  g: GameState,
  hooks: GameHooks,
  med: Plane,
  ri: number,
): boolean {
  const r = g.runways[ri]!;
  const c = canLand(med, r);
  if (!c.ok) {
    hooks.toast(
      `${med.cs}: ${r.name} еще занята — заход начнется, когда освободится (или кликните полосу)`,
      "wx",
    );
    return false;
  }
  cmdLand(g, hooks, med, ri, { force: true });
  hooks.toast(`${med.cs}: заход на ${r.name} разрешен`, "good");
  return true;
}

/** Освободить основную полосу под медрейс. */
export function clearRunwayForMed(
  g: GameState,
  hooks: GameHooks,
  ri: number,
): Plane {
  const r = g.runways[ri]!;
  const other = g.runways[1 - ri]!;
  let diverted = 0;
  for (const p of g.planes) {
    if (p.dead || p.medevac) continue;
    if (p.rwy === ri && p.status === "inbound") {
      divertForMed(g, hooks, p, `полоса ${r.name} под медрейс`);
      diverted++;
    }
  }
  for (const p of g.planes) {
    if (p.dead) continue;
    if (p.status === "hold_short" && p.rwyWish === ri) {
      hooks.toast(
        `${p.cs} ждет: ${r.name} зарезервирована под медрейс`,
        "wx",
      );
    }
  }
  const med = spawnMedevac(g, hooks, "Медрейс");
  r.reservedFor = med.id;
  r.reserveT = 28;
  r.reserveLabel = med.cs;
  hooks.toast(
    `${r.name} зарезервирована под ${med.cs} на ~28с${diverted ? ` · отведено заходов: ${diverted}` : ""} · ${other.name} для остальных`,
    "good",
  );
  g.bestDecision = "Освобождение полосы под медрейс";
  tryAutoLandMed(g, hooks, med, ri);
  return med;
}

/** Направить медрейс на менее загруженную полосу без полного отвода. */
export function routeMedToSpare(g: GameState, hooks: GameHooks): Plane {
  const load = (ri: number) =>
    g.planes.filter(
      (p) =>
        !p.dead &&
        p.rwy === ri &&
        (p.status === "inbound" ||
          p.status === "landing" ||
          p.status === "takeoff"),
    ).length;
  const ri = load(0) <= load(1) ? 0 : 1;
  const r = g.runways[ri]!;
  const med = spawnMedevac(g, hooks, "Медрейс (запасной контур)");
  r.reservedFor = med.id;
  r.reserveT = 22;
  r.reserveLabel = med.cs;
  g.riskyOps++;
  hooks.toast(
    `${med.cs} направлен на ${r.name} без полного освобождения основной — риск выше`,
    "bad",
  );
  g.worstMistake = "Медрейс без полной очистки основной полосы";
  tryAutoLandMed(g, hooks, med, ri);
  return med;
}

/** Колода карточек решений. */
export const EVENT_DECK: readonly DecisionDef[] = [
  {
    id: "wind",
    title: "Резкий порыв ветра",
    text: "Боковой ветер усиливается. Можно ограничить взлеты или продолжить с риском удлинения пробега.",
    opts: [
      {
        label: "Ограничить взлеты на 20с",
        tip: "безопаснее",
        run({ g, hooks }) {
          g.cross = 1;
          g.crossT = 20;
          g.takeoffLockT = Math.max(g.takeoffLockT || 0, 20);
          hooks.toast("Взлеты ограничены на 20с из‑за ветра");
          g.bestDecision = "Сдержанность при боковом ветре";
        },
      },
      {
        label: "Продолжить работу",
        tip: "риск",
        run({ g, hooks }) {
          g.cross = 1;
          g.crossT = 28;
          g.riskyOps++;
          g.windSpd += 8;
          hooks.toast("Работаем в боковой ветер — риск повышен", "bad");
          g.worstMistake = "Игнорирование бокового ветра";
        },
      },
    ],
  },
  {
    id: "plowbreak",
    title: "Сбой уборки полос",
    text: "Уборочная техника на пределе — возможна пауза в очистке ВПП.",
    opts: [
      {
        label: "Пауза на обслуживание",
        tip: "уборка недоступна ~22с",
        run({ g, hooks }) {
          g.clearLockT = Math.max(g.clearLockT, 22 / g.cfg.recover);
          hooks.toast("Уборка полос временно недоступна");
        },
      },
      {
        label: "Работать на износ",
        tip: "риск более долгого простоя",
        run({ g, hooks }) {
          g.riskyOps++;
          hooks.toast("Технику оставили в строю", "bad");
          if (Math.random() < 0.45) {
            g.clearLockT = Math.max(g.clearLockT, 35);
            hooks.toast("Уборка встала надолго", "bad");
            g.worstMistake = "Перегруз уборочной техники";
          }
        },
      },
    ],
  },
  {
    id: "reagent",
    title: "Склад реагента на исходе",
    text: "Запас противогололедного реагента для полос почти кончился. Без него нельзя снять гололед с ВПП (кнопка «Реагент»). Это не обработка самолета — борта обрабатываются отдельно в карточке.",
    opts: [
      {
        label: "Экономить запас",
        tip: "остаток делим пополам",
        run({ g, hooks }) {
          g.reagent = Math.max(12, g.reagent * 0.55);
          hooks.toast(
            "Реагент экономят — хватит на меньше обработок полос",
          );
        },
      },
      {
        label: "Заказать срочную поставку",
        tip: "+45% запаса, −40 очков",
        run({ g, hooks }) {
          g.reagent = Math.min(100, g.reagent + 45);
          addScore(g, hooks, -40, null, "ПОСТАВКА РЕАГЕНТА");
          hooks.toast("Поставка: запас реагента пополнен", "good");
        },
      },
    ],
  },
  {
    id: "emergency",
    title: "Экстренный запрос на посадку",
    text: "Медицинский борт запрашивает немедленную посадку. «Освободить основную» чистит северную полосу (отвод чужих заходов, резерв ~28с) и сразу дает заход медрейсу, если полоса свободна. «Запасная» — на менее занятую полосу без полного отвода, риск выше. Если полоса занята, заход начнется, как только она освободится.",
    opts: [
      {
        label: "Освободить основную полосу",
        tip: "отвод + резерв + заход",
        run({ g, hooks }) {
          clearRunwayForMed(g, hooks, 0);
        },
      },
      {
        label: "Направить на запасную",
        tip: "без полного отвода, риск↑",
        run({ g, hooks }) {
          routeMedToSpare(g, hooks);
        },
      },
    ],
  },
  {
    id: "crew",
    title: "Экипаж требует обработку",
    text: "Готовый к вылету борт отказывается рулить без противообледенительной обработки крыла (это не реагент на полосе).",
    opts: [
      {
        label: "Назначить обработку",
        tip: "задержка, безопасность",
        run({ g, hooks }) {
          const p = g.planes.find(
            (x) =>
              !x.dead &&
              (x.status === "ready" || x.status === "hold_short") &&
              !x.deiced &&
              !x.deiceWaive,
          );
          if (p) {
            p.needsDeice = true;
            p.deiced = false;
            p.deiceWaive = false;
            hooks.toast(
              `${p.cs}: требуется обработка от обледенения перед вылетом`,
            );
          }
        },
      },
      {
        label: "Разрешить без обработки",
        tip: "риск инцидента",
        run({ g, hooks }) {
          const p = g.planes.find(
            (x) =>
              !x.dead &&
              (x.status === "ready" || x.status === "hold_short") &&
              (x.needsDeice || !x.deiced) &&
              !x.deiceWaive,
          );
          if (p) {
            p.deiceWaive = true;
            p.needsDeice = false;
            hooks.toast(
              `${p.cs}: вылет без обработки разрешен — риск повышен`,
              "bad",
            );
          } else {
            hooks.toast("Вылет без обработки — сцепление на грани", "bad");
          }
          g.riskyOps += 2;
          g.worstMistake =
            "Вылет без противообледенительной обработки";
        },
      },
    ],
  },
  {
    id: "lights",
    title: "Сбой огней полосы",
    text: "Часть огней на одной из полос мигает в аварийном режиме.",
    opts: [
      {
        label: "Закрыть полосу на 15с",
        tip: "безопасно",
        run({ g, hooks }) {
          scheduleRwyTempClose(g, hooks, Math.random() < 0.5 ? 0 : 1, 15);
        },
      },
      {
        label: "Работать по резервной схеме",
        tip: "видимость↓",
        run({ g, hooks }) {
          g.visTarget = Math.max(g.visTarget, 0.32);
          g.riskyOps++;
          hooks.toast("Полоса работает при неисправных огнях", "bad");
        },
      },
    ],
  },
  {
    id: "taxiJam",
    title: "Скопление на рулежке",
    text: "Несколько бортов сошлись у перрона.",
    opts: [
      {
        label: "Остановить руление на 10с",
        tip: "развести потоки",
        run({ g, hooks }) {
          for (const p of g.planes) {
            if (p.status === "taxi_gate" || p.status === "taxi_rwy") {
              p.yieldT = Math.max(p.yieldT, 10);
            }
          }
          hooks.toast("Руление приостановлено для развода");
          g.bestDecision = "Разведение потока на рулежке";
        },
      },
      {
        label: "Проталкивать очередь",
        tip: "риск сближения",
        run({ g, hooks }) {
          g.riskyOps++;
          g.paxMood -= 8;
          hooks.toast(
            "Плотное руление сохранено — риск сближений",
            "bad",
          );
        },
      },
    ],
  },
];

/** Подходит ли событие к текущей ситуации. */
export function eventEligible(g: GameState, ev: DecisionDef): boolean {
  if (ev.id === "reagent") return g.reagent < 38;
  if (ev.id === "crew") {
    const ice = Math.max(g.runways[0]?.ice || 0, g.runways[1]?.ice || 0);
    const stage = frontStage(g).id;
    const harsh =
      ice > 28 ||
      stage === "peak" ||
      stage === "build" ||
      g.vis > 0.28;
    return (
      harsh &&
      g.planes.some(
        (p) =>
          !p.dead &&
          (p.status === "ready" || p.status === "hold_short") &&
          !p.deiced &&
          !p.deiceWaive,
      )
    );
  }
  if (ev.id === "taxiJam") {
    const taxi = g.planes.filter(
      (p) =>
        !p.dead &&
        (p.status === "taxi_gate" ||
          p.status === "taxi_rwy" ||
          p.status === "awaiting_gate"),
    );
    return taxi.length >= 2;
  }
  if (ev.id === "emergency") {
    const medBusy = g.planes.some(
      (p) =>
        !p.dead &&
        p.medevac &&
        (p.status === "approach" ||
          p.status === "inbound" ||
          p.status === "landing"),
    );
    const reserved = g.runways.some((r) => r.reservedFor != null);
    return !medBusy && !reserved && g.planes.filter((p) => !p.dead).length < 10;
  }
  return true;
}

/**
 * Выбрать и показать карточку решения.
 * @returns true, если карточка открыта
 */
export function pushDecision(g: GameState, hooks: GameHooks): boolean {
  if (!g || g.decision || g.mode === "tutorial" || g.over) return false;
  const pool = EVENT_DECK.filter((ev) => eventEligible(g, ev));
  if (!pool.length) return false;
  const ev = pick(pool);
  g.running = false;
  Snd.warn();
  hooks.showDecision?.(ev);
  // Заглушка decision, пока UI не очистит карточку
  g.decision = {
    id: ev.id,
    title: ev.title,
    text: ev.text,
    opts: [],
  };
  return true;
}

/** Случайный джиттер таймера следующего события. */
export function nextEventDelay(phase: number, eventsMul: number): number {
  const base =
    phase === 0 ? rnd(26, 36) : phase === 1 ? rnd(18, 28) : rnd(14, 22);
  return base / eventsMul;
}
